/**
 * Notifications API Schemas
 *
 * Schemas for notification events and multi-channel delivery.
 * Supports in-app notifications, email, Bitrix24, and push notifications.
 *
 * Endpoints:
 * - GET /api/v1/notifications - List notifications for user
 * - GET /api/v1/notifications/count - Get unread count
 * - POST /api/v1/notifications/:id/read - Mark notification as read
 * - POST /api/v1/notifications/read-all - Mark all as read
 */

import { z } from 'zod/v4';

// ============================================================================
// Enums
// ============================================================================

/**
 * Notification severity levels
 *
 * - `low`: Informational (e.g., record completed)
 * - `medium`: Requires attention (e.g., quality score below threshold)
 * - `high`: Urgent (e.g., pipeline failure)
 * - `critical`: Immediate action required (e.g., budget exceeded)
 */
export const notificationSeveritySchema = z.enum(['low', 'medium', 'high', 'critical']);

/**
 * Notification status lifecycle
 *
 * - `pending`: Created but not yet delivered
 * - `delivered`: Sent to notification channels
 * - `read`: User has acknowledged the notification
 * - `archived`: User has dismissed/archived the notification
 */
export const notificationStatusSchema = z.enum([
  'pending',
  'delivered',
  'read',
  'archived',
]);

/**
 * Notification categories for filtering and routing
 *
 * - `quality_alerts`: QA score below threshold, compliance issues
 * - `pipeline_events`: Record processing updates (transcribed, analyzed)
 * - `system_health`: Worker failures, API errors
 * - `budget_billing`: Cost thresholds, billing alerts
 * - `collaboration`: Mentions, comments, shared links
 * - `administrative`: User invites, role changes
 * - `incidents`: Critical incidents requiring attention
 * - `compliance_audit`: Audit trail events, security alerts
 */
export const notificationCategorySchema = z.enum([
  'quality_alerts',
  'pipeline_events',
  'system_health',
  'budget_billing',
  'collaboration',
  'administrative',
  'incidents',
  'compliance_audit',
]);

// ============================================================================
// Core Schemas
// ============================================================================

/**
 * Notification event
 *
 * Represents a notification that can be delivered via multiple channels.
 * The notification_events table stores the event itself (org-scoped),
 * while notification_user_status tracks per-user read status.
 *
 * Database Tables:
 * - notification_events: Core notification data (orgId, not userId)
 * - notification_user_status: Per-user read status (userId, status, readAt)
 * - delivery_log: Multi-channel delivery tracking (email, Bitrix24, push)
 */
export const notificationEventSchema = z.object({
  id: z.string(),
  /** Notification type identifier (e.g., 'record.transcribed', 'alert.triggered') */
  type: z.string(),
  orgId: z.string(),
  /** Optional project scope for filtering */
  projectId: z.string().optional(),
  /** Associated record ID (e.g., call/chat that triggered notification) */
  recordId: z.string().optional(),
  /** Array of user IDs to receive notification (if null, all org users) */
  recipients: z.array(z.string()).optional(),
  /**
   * Text rendered from the call when the notification was created. Absent for
   * a narrowed access grant (E1.5, #1336) that does not cover everything the
   * text could name — the record, the evidence, the employee: the row stays a
   * row (type, severity, read state), so the inbox still counts right.
   */
  title: z.string().optional(),
  message: z.string().optional(),
  severity: notificationSeveritySchema,
  category: z.string(),
  status: notificationStatusSchema,
  /** Optional deep link to related resource (e.g., /records/:id) */
  actionUrl: z.string().optional(),
  /** Structured data for notification (e.g., { qualityScore: 45, threshold: 70 }) */
  payload: z.record(z.string(), z.unknown()).optional(),
  /** Additional metadata for filtering/routing (e.g., { channel: 'email' }) */
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string(),
  /** Per-user status (added by getRecentUnread/listForUser queries) */
  userStatus: notificationStatusSchema.optional(),
  /** Timestamp when user marked notification as read */
  readAt: z.string().optional(),
});

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Query parameters for listing notifications - GET /api/v1/notifications
 */
export const listNotificationsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
  /** Filter by notification status (read, unread, archived) */
  status: notificationStatusSchema.optional(),
  /** Filter by category (quality_alerts, pipeline_events, etc.) */
  category: z.string().optional(),
});

/**
 * Request body for marking notification as read - POST /api/v1/notifications/:id/read
 */
export const markNotificationReadRequestSchema = z
  .object({
    notificationId: z.string().uuid(),
  })
  .strict();

/**
 * Request body for marking all notifications as read - POST /api/v1/notifications/read-all
 */
export const markAllReadRequestSchema = z
  .object({
    /** Optional category filter to mark only specific category as read */
    category: z.string().optional(),
  })
  .strict();

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Response for listing notifications - matches GET /api/v1/notifications
 */
export const notificationsListResponseSchema = z.object({
  items: z.array(notificationEventSchema),
  total: z.number().optional(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
});

/**
 * Response for unread count - matches GET /api/v1/notifications/count
 */
export const notificationCountResponseSchema = z.object({
  count: z.number(),
});

/**
 * Response for single notification operations
 */
export const notificationResponseSchema = z.object({
  notification: notificationEventSchema,
});

// ============================================================================
// Type exports
// ============================================================================

export type NotificationSeverity = z.infer<typeof notificationSeveritySchema>;
export type NotificationStatus = z.infer<typeof notificationStatusSchema>;
export type NotificationCategory = z.infer<typeof notificationCategorySchema>;
export type NotificationEvent = z.infer<typeof notificationEventSchema>;
export type ListNotificationsQuery = z.infer<typeof listNotificationsQuerySchema>;
export type NotificationsListResponse = z.infer<typeof notificationsListResponseSchema>;
export type NotificationCountResponse = z.infer<typeof notificationCountResponseSchema>;
